import {
  bearing as turfBearing,
  distance as turfDistance,
  lineString as turfLineString,
  point as turfPoint,
  pointToLineDistance
} from '@turf/turf';
import { PROFILE_LINE, TurnInstruction, isJoinEntry } from '../turnInstructions';
import { ManeuverSign, isTerminal, signForAngle } from './maneuverSign';

/**
 * Removes the out-and-back stub GraphHopper draws when a marker lands just past a junction.
 *
 * Each segment is routed marker to marker, so a marker a few metres past a corner gets a
 * line that runs to it and a next line that runs back. GraphHopper describes both relative
 * to the stub — a turn into it at the end of one segment, a U-turn out of it at the start of
 * the next — and the recording apps drop the U-turn as a segment opening, so the hiker is
 * told to turn the wrong way.
 *
 * The thresholds come from measuring stored routes, and every one of them is shared with
 * the phones — the same hike is planned on one platform and walked on another. Artifact
 * stubs ran up to ~11 m; genuine turnarounds started at ~13 m and still reversed after the
 * stub was removed.
 */

export const SPUR_MAX_METERS = 12;
export const SPUR_TOLERANCE_METERS = 1.5;
/** The shortest segment the phone planners allow. A trim never produces a shorter one. */
export const MIN_SEGMENT_METERS = 5;
/** A turn this sharp at the junction is a turnaround the route really makes, not a stub. */
export const TURNAROUND_DEGREES = 155;

const HEADING_WINDOW_METERS = 20;
const AT_JUNCTION_METERS = 0.5;
/** Two vertices this close are the same map node, as GraphHopper repeats it either side of a marker. */
const SAME_VERTEX_METERS = 0.2;

export interface SpurSegment {
  coordinates: number[][];
  instructions: TurnInstruction[];
  /** "hike", "bike" or "car" when snapped, "line" for a phone's straight line. */
  profile?: string | null;
}

export interface TrimmedJoin {
  a: SpurSegment;
  b: SpurSegment;
  junction: number[];
  /** How many coordinates were dropped from the start of B. */
  bDroppedCount: number;
  removedAMeters: number;
  removedBMeters: number;
  /**
   * The stub could not be cut without leaving a segment shorter than `MIN_SEGMENT_METERS`, so
   * the geometry and the marker stay as drawn and only the turns are put right.
   */
  turnsOnly: boolean;
}

/**
 * The join between two snapped segments with its stub removed, or null when there is none or
 * nothing to change.
 */
export function trimJoinSpur(a: SpurSegment, b: SpurSegment): TrimmedJoin | null {
  if (!isSnapped(a) || !isSnapped(b)) return null;
  if (a.coordinates.length < 2 || b.coordinates.length < 2) return null;

  const cut = findSpur(a.coordinates, b.coordinates);
  if (!cut) return null;

  const newA = cut.appended
    ? [...a.coordinates.slice(0, cut.aIndex + 1), cut.appended]
    : a.coordinates.slice(0, cut.aIndex + 1);
  const newB = b.coordinates.slice(cut.bIndex);
  if (newA.length < 2 || newB.length < 2) return null;

  const newALength = pathLength(newA);
  const newBLength = pathLength(newB);
  const turnsOnly = newALength < MIN_SEGMENT_METERS || newBLength < MIN_SEGMENT_METERS;

  const removedA = pathLength(a.coordinates) - newALength;
  const removedB = pathLength(b.coordinates) - newBLength;

  const junction = newA[newA.length - 1];
  const angle = junctionTurn(newA, newB);
  if (angle === null || Math.abs(angle) >= TURNAROUND_DEGREES) return null;

  const scaleA = capturedScale(a);
  const scaleB = capturedScale(b);

  const intoSpur = a.instructions.filter(
    (turn) => !isTerminal(turn.sign) && turn.d / scaleA >= newALength - AT_JUNCTION_METERS
  );
  const outOfSpur = b.instructions.filter(
    (turn, index) =>
      index > 0 && !isTerminal(turn.sign) && turn.d / scaleB <= removedB + AT_JUNCTION_METERS
  );
  if ([...intoSpur, ...outOfSpur].some(isAuthored)) return null;

  const junctionD = round(newALength * scaleA);
  const aTurns = a.instructions
    .filter((turn) => !intoSpur.includes(turn))
    .map((turn) => (isTerminal(turn.sign) && !turnsOnly ? { ...turn, d: junctionD } : turn));

  const sign = signForAngle(angle);
  if (sign !== ManeuverSign.CONTINUE) {
    // On a later pass the turn out of the stub is gone, so the junction turn written last time
    // is what still knows the road.
    const written = turnsOnly
      ? intoSpur.find((turn) => turn.sign === sign && Math.abs(turn.d - junctionD) < 0.15)
      : undefined;
    const street = outOfSpur.find((turn) => turn.street)?.street ?? written?.street ?? b.instructions[0]?.street;
    const turn: TurnInstruction = { d: junctionD, sign, text: '' };
    if (street) turn.street = street;
    const terminalIndex = aTurns.findIndex((entry) => isTerminal(entry.sign));
    aTurns.splice(terminalIndex >= 0 ? terminalIndex : aTurns.length, 0, turn);
  }

  if (turnsOnly) {
    const bTurns = b.instructions.filter((turn) => !outOfSpur.includes(turn));
    const unchanged =
      JSON.stringify(aTurns) === JSON.stringify(a.instructions) &&
      JSON.stringify(bTurns) === JSON.stringify(b.instructions);
    if (unchanged) return null;
    return {
      a: { coordinates: a.coordinates, instructions: aTurns, profile: a.profile },
      b: { coordinates: b.coordinates, instructions: bTurns, profile: b.profile },
      junction,
      bDroppedCount: 0,
      removedAMeters: 0,
      removedBMeters: 0,
      turnsOnly
    };
  }

  const shift = removedB * scaleB;
  const bTurns = b.instructions
    .filter((turn) => !outOfSpur.includes(turn))
    .map((turn, index) =>
      index === 0 ? { ...turn, d: 0 } : { ...turn, d: Math.max(0, round(turn.d - shift)) }
    );

  return {
    a: { coordinates: newA, instructions: aTurns, profile: a.profile },
    b: { coordinates: newB, instructions: bTurns, profile: b.profile },
    junction,
    bDroppedCount: cut.bIndex,
    removedAMeters: removedA,
    removedBMeters: removedB,
    turnsOnly
  };
}

interface LineStringLike {
  lineStringGeometry: { coordinates: number[][] };
  lineStringProperties: {
    instructions?: TurnInstruction[];
    profile?: string | null;
    extras?: { surface?: { values?: any[] }; distances?: any[] };
    summary?: { distance: number };
  };
}

interface MarkerLike {
  markerGeometry: { coordinates: number[] };
}

/**
 * Trim every join of a route in place, walking order.
 *
 * In order because a segment can be trimmed at both ends: the second join sees the segment
 * the first one left, so the minimum length is judged on what remains after both. The marker
 * at a trimmed join moves to the junction, which is what stops the next re-snap drawing the
 * stub again. Returns whether anything changed.
 */
export function applyJoinSpurTrims(lineStrings: LineStringLike[], markers: MarkerLike[]): boolean {
  if (lineStrings.length < 2 || markers.length !== lineStrings.length + 1) return false;

  let changed = false;
  for (let k = 0; k < lineStrings.length - 1; k++) {
    const first = lineStrings[k];
    const second = lineStrings[k + 1];
    const trimmed = trimJoinSpur(segmentOf(first), segmentOf(second));
    if (!trimmed) continue;
    if (trimmed.turnsOnly) {
      first.lineStringProperties.instructions = trimmed.a.instructions;
      second.lineStringProperties.instructions = trimmed.b.instructions;
      changed = true;
      continue;
    }

    writeBack(first, trimmed.a, trimmed.removedAMeters, (values) =>
      keepHead(values, trimmed.a.coordinates.length)
    );
    writeBack(second, trimmed.b, trimmed.removedBMeters, (values) =>
      dropHead(values, trimmed.bDroppedCount)
    );

    const marker = markers[k + 1].markerGeometry;
    const elevation = marker.coordinates[2];
    marker.coordinates =
      elevation === undefined
        ? [trimmed.junction[0], trimmed.junction[1]]
        : [trimmed.junction[0], trimmed.junction[1], elevation];
    changed = true;
  }
  return changed;
}

function segmentOf(lineString: LineStringLike): SpurSegment {
  return {
    coordinates: lineString.lineStringGeometry?.coordinates ?? [],
    instructions: lineString.lineStringProperties?.instructions ?? [],
    profile: lineString.lineStringProperties?.profile
  };
}

function writeBack(
  lineString: LineStringLike,
  segment: SpurSegment,
  removedMeters: number,
  reindex: (values: any[]) => any[]
) {
  lineString.lineStringGeometry.coordinates = segment.coordinates;
  const properties = lineString.lineStringProperties;
  properties.instructions = segment.instructions;
  if (properties.extras?.surface?.values) {
    properties.extras.surface.values = reindex(properties.extras.surface.values);
  }
  if (properties.extras?.distances) {
    properties.extras.distances = reindex(properties.extras.distances);
  }
  if (properties.summary) {
    properties.summary.distance = Math.max(0, properties.summary.distance - removedMeters);
  }
}

/**
 * `[start, end, …]` index ranges for a line cut down to its first `count` coordinates.
 *
 * Two conventions share this field: GraphHopper's ranges end on the last coordinate they
 * cover, ranges rebuilt when a saved hike loads end one past it. Clamping to `count` is right
 * for the second and harmless for the first, since the save never reads past the line.
 */
function keepHead(values: any[], count: number): any[] {
  return values
    .filter((range) => Array.isArray(range) && range[0] < count)
    .map(([start, end, ...rest]) => [start, Math.min(end, count), ...rest]);
}

/** `[start, end, …]` index ranges for a line with its first `count` coordinates removed. */
function dropHead(values: any[], count: number): any[] {
  return values
    .filter((range) => Array.isArray(range) && range[1] - count > 0)
    .map(([start, end, ...rest]) => [Math.max(0, start - count), end - count, ...rest]);
}

interface SpurCut {
  /** Last coordinate of A to keep. */
  aIndex: number;
  /** First coordinate of B to keep. */
  bIndex: number;
  /** A point to end A on, when the cut falls between two of its coordinates. */
  appended?: number[];
}

function findSpur(a: number[][], b: number[][]): SpurCut | null {
  const last = a.length - 1;
  if (meters(a[last], b[0]) <= SPUR_TOLERANCE_METERS) return retrace(a, b);
  return startsOnTail(a, b);
}

/**
 * Walk back along A and forward along B while they lie on top of each other.
 *
 * Only after a U-turn at the tip: a join the route passes straight through can have
 * vertices close enough together to look like overlap, but it never reverses.
 */
function retrace(a: number[][], b: number[][]): SpurCut | null {
  if (!turnsBackAtJoin(a, b)) return null;

  const last = a.length - 1;
  let i = last;
  let j = 0;
  let length = 0;
  while (i > 0 && j < b.length - 1) {
    let nextI = i;
    let nextJ = j;
    if (meters(a[i - 1], b[j + 1]) <= SPUR_TOLERANCE_METERS) {
      nextI = i - 1;
      nextJ = j + 1;
    } else if (toSegment(a[i - 1], b[j], b[j + 1]) <= SPUR_TOLERANCE_METERS) {
      nextI = i - 1;
    } else if (toSegment(b[j + 1], a[i - 1], a[i]) <= SPUR_TOLERANCE_METERS) {
      nextJ = j + 1;
    } else {
      break;
    }
    if (nextI !== i) {
      length += meters(a[nextI], a[i]);
      // Longer than any artifact: a real out-and-back, left exactly as drawn.
      if (length > SPUR_MAX_METERS) return null;
    }
    i = nextI;
    j = nextJ;
  }
  if (i === last || j === 0) return null;
  return { aIndex: i, bIndex: j };
}

/**
 * Whether the route reverses at the join. A marker under 0.5 m past a junction has no heading of
 * its own, so that case is caught by A arriving and B leaving through the same vertex.
 */
export function turnsBackAtJoin(a: number[][], b: number[][]): boolean {
  if (a.length < 2 || b.length < 2) return false;
  const last = a.length - 1;
  if (meters(a[last - 1], b[1]) <= SAME_VERTEX_METERS) return true;

  const tipIn = headingBefore(a, last);
  const tipOut = headingAfter(b, 0);
  if (tipIn === null || tipOut === null) return false;
  return Math.abs(signedTurn(tipIn, tipOut)) >= TURNAROUND_DEGREES;
}

/**
 * B already starts at the junction but A still runs on to the old marker — one side
 * re-snapped after a trim. A is cut back to where B begins.
 */
function startsOnTail(a: number[][], b: number[][]): SpurCut | null {
  let length = 0;
  for (let k = a.length - 2; k >= 0; k--) {
    if (toSegment(b[0], a[k], a[k + 1]) <= SPUR_TOLERANCE_METERS) {
      if (meters(a[k + 1], b[0]) <= AT_JUNCTION_METERS) return { aIndex: k + 1, bIndex: 0 };
      if (meters(a[k], b[0]) <= AT_JUNCTION_METERS) return { aIndex: k, bIndex: 0 };
      return { aIndex: k, bIndex: 0, appended: b[0] };
    }
    length += meters(a[k], a[k + 1]);
    if (length > SPUR_MAX_METERS) return null;
  }
  return null;
}

/** The turn a walker makes at the junction, measured over a stretch either side. */
function junctionTurn(a: number[][], b: number[][]): number | null {
  const junction = a[a.length - 1];
  const before = pointBack(a, HEADING_WINDOW_METERS);
  const after = pointAhead(b, HEADING_WINDOW_METERS);
  if (!before || !after) return null;
  return signedTurn(bearing(before, junction), bearing(junction, after));
}

function pointBack(coordinates: number[][], window: number): number[] | null {
  const end = coordinates[coordinates.length - 1];
  let length = 0;
  let found: number[] | null = null;
  for (let k = coordinates.length - 2; k >= 0; k--) {
    length += meters(coordinates[k], coordinates[k + 1]);
    if (meters(coordinates[k], end) > AT_JUNCTION_METERS) found = coordinates[k];
    if (found && length >= window) break;
  }
  return found;
}

function pointAhead(coordinates: number[][], window: number): number[] | null {
  const start = coordinates[0];
  let length = 0;
  let found: number[] | null = null;
  for (let k = 1; k < coordinates.length; k++) {
    length += meters(coordinates[k - 1], coordinates[k]);
    if (meters(coordinates[k], start) > AT_JUNCTION_METERS) found = coordinates[k];
    if (found && length >= window) break;
  }
  return found;
}

function headingBefore(coordinates: number[][], index: number): number | null {
  for (let k = index - 1; k >= 0; k--) {
    if (meters(coordinates[k], coordinates[index]) > AT_JUNCTION_METERS) {
      return bearing(coordinates[k], coordinates[index]);
    }
  }
  return null;
}

function headingAfter(coordinates: number[][], index: number): number | null {
  for (let k = index + 1; k < coordinates.length; k++) {
    if (meters(coordinates[index], coordinates[k]) > AT_JUNCTION_METERS) {
      return bearing(coordinates[index], coordinates[k]);
    }
  }
  return null;
}

/**
 * Whether GraphHopper routed this segment, which a straight line or a pre-turn-by-turn
 * segment never was.
 *
 * Decided by profile, not wording: a reversed segment's turns are mirrored, and mirroring
 * blanks every `text` by design. Wording is only the fallback for segments stored before the
 * profile was — a web straight line has no profile either, but its cue bookends are unworded.
 */
export function isSnapped(segment: SpurSegment): boolean {
  const routed = segment.instructions.some((turn) => turn.cue == null);
  if (!routed) return false;
  if (segment.profile) return segment.profile !== PROFILE_LINE;
  return segment.instructions.some((turn) => turn.cue == null && turn.text.trim() !== '');
}

function isAuthored(turn: TurnInstruction): boolean {
  if (isJoinEntry(turn)) return false;
  return turn.cue != null || !!turn.utext || turn.usign != null || !!turn.hidden;
}

/**
 * Stored distances over geometry distances. Turns captured on a phone run a few percent off
 * the geometry, so a length cut from the line has to be converted before it moves a turn.
 */
export function capturedScale(segment: SpurSegment): number {
  let captured: number | undefined;
  for (let k = segment.instructions.length - 1; k >= 0; k--) {
    if (isTerminal(segment.instructions[k].sign)) {
      captured = segment.instructions[k].d;
      break;
    }
  }
  const geometry = pathLength(segment.coordinates);
  if (!captured || captured <= 0 || geometry <= 0) return 1;
  return captured / geometry;
}

export function pathLength(coordinates: number[][]): number {
  let total = 0;
  for (let k = 1; k < coordinates.length; k++) total += meters(coordinates[k - 1], coordinates[k]);
  return total;
}

function meters(p: number[], q: number[]): number {
  return turfDistance([p[0], p[1]], [q[0], q[1]], { units: 'meters' });
}

function toSegment(p: number[], start: number[], end: number[]): number {
  if (meters(start, end) < 0.01) return meters(p, start);
  return pointToLineDistance(
    turfPoint([p[0], p[1]]),
    turfLineString([
      [start[0], start[1]],
      [end[0], end[1]]
    ]),
    { units: 'meters' }
  );
}

function bearing(p: number[], q: number[]): number {
  return turfBearing(turfPoint([p[0], p[1]]), turfPoint([q[0], q[1]]));
}

/** Turn angle in (-180, 180]; positive is a right turn, matching `signForAngle`. */
function signedTurn(incoming: number, outgoing: number): number {
  let delta = outgoing - incoming;
  while (delta <= -180) delta += 360;
  while (delta > 180) delta -= 360;
  return delta;
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}
